import { create } from "zustand";
import { persist } from "zustand/middleware";
import { axiosInstancev1 } from "../lib/axiosInstance";
import useAuthStore from "./useAuthStore";
import { toast } from "react-hot-toast";

const useFindBuilderStore = create(
  persist(
    (set, get) => ({
      states: [],
      districts: [],
      taluks: [],
      selectedState: "",
      selectedDistrict: "",
      selectedTaluk: "",
      isLoading: false,
      builders: [],
      builder: null,
      mappedDistricts: [],
      mappedTaluks: [],
      defaultServices: [],
      filterExperience: [],
      filterServiceIds: [],
      filterVerifiedOnly: false,
      pagination: { total: 0, currentPage: 1, totalPages: 0 },

      setSelectedState: (state) => {
        set({ selectedState: state });
      },
      setSelectedDistrict: (district) => {
        const currentDistrict = get().selectedDistrict;
        if (district !== currentDistrict) {
          set({ selectedDistrict: district, selectedTaluk: "" });
        } else {
          set({ selectedDistrict: district });
        }
      },
      setSelectedTaluk: (taluk) => {
        set({ selectedTaluk: taluk });
      },

      setFilterExperience: (bands) => set({ filterExperience: bands }),
      setFilterServiceIds: (ids) => set({ filterServiceIds: ids }),
      setFilterVerifiedOnly: (value) => set({ filterVerifiedOnly: value }),
      clearFilters: () =>
        set({
          filterExperience: [],
          filterServiceIds: [],
          filterVerifiedOnly: false,
        }),

      fetchLocationData: async () => {
        set({ isLoading: true });
        try {
          const response = await axiosInstancev1.get("/address/location-data");
          set({
            states: response.data.states.length ? response.data.states : ["No States Available"],
            districts: response.data.districts.length ? response.data.districts : ["No Districts Available"],
            taluks: response.data.taluks.length ? response.data.taluks : ["No Taluks Available"],
          });
        } catch (error) {
          console.error("Error fetching location data:", error);
        } finally {
          set({ isLoading: false });
        }
      },

      fetchDefaultServices: async () => {
        try {
          const response = await axiosInstancev1.get("/user/default-services");
          set({ defaultServices: response.data.services || [] });
        } catch (error) {
          console.error("Error fetching default services:", error);
          set({ defaultServices: [] });
        }
      },

      fetchBuilders: async (options = {}) => {
        const {
          selectedDistrict,
          selectedTaluk,
          filterExperience,
          filterServiceIds,
          filterVerifiedOnly,
        } = get();

        if (!selectedDistrict || !selectedTaluk) {
          set({ builders: [], pagination: { total: 0, currentPage: 1, totalPages: 0 } });
          return;
        }

        const page = options.page || 1;
        const limit = options.limit || 48;

        const baseParams = {
          district: selectedDistrict || "",
          taluk: selectedTaluk || "",
          sortDir: "asc",
          limit: String(limit),
          page: String(page),
          enquiry: "true",
        };

        if (filterVerifiedOnly) {
          baseParams.verifiedOnly = "true";
        }

        const experienceBands =
          filterExperience?.length === 1 ? filterExperience[0] : null;

        const serviceIds =
          filterServiceIds?.length === 1 ? filterServiceIds[0] : null;

        const runQuery = async (extra = {}) => {
          const params = new URLSearchParams({ ...baseParams, ...extra }).toString();
          const response = await axiosInstancev1.get(`/user/builders?${params}`);
          return response.data;
        };

        set({ isLoading: true });
        try {
          let data;

          if (experienceBands && serviceIds) {
            const [expData, svcData] = await Promise.all([
              runQuery({ experienceBand: experienceBands, serviceId: String(serviceIds) }),
              null,
            ]);
            data = expData;
          } else if (experienceBands) {
            data = await runQuery({ experienceBand: experienceBands });
          } else if (serviceIds) {
            data = await runQuery({ serviceId: String(serviceIds) });
          } else if (filterExperience?.length > 1) {
            const results = await Promise.all(
              filterExperience.map((band) => runQuery({ experienceBand: band }))
            );
            const merged = new Map();
            results.forEach((r) => {
              (r.builders || []).forEach((b) => merged.set(b.builder_id, b));
            });
            data = {
              builders: Array.from(merged.values()),
              pagination: { total: merged.size, currentPage: 1, totalPages: 1 },
            };
          } else if (filterServiceIds?.length > 1) {
            const results = await Promise.all(
              filterServiceIds.map((id) => runQuery({ serviceId: String(id) }))
            );
            const merged = new Map();
            results.forEach((r) => {
              (r.builders || []).forEach((b) => merged.set(b.builder_id, b));
            });
            data = {
              builders: Array.from(merged.values()),
              pagination: { total: merged.size, currentPage: 1, totalPages: 1 },
            };
          } else {
            data = await runQuery();
          }

          if (data?.builders) {
            set({
              builders: data.builders,
              pagination: data.pagination || {
                total: data.builders.length,
                currentPage: page,
                totalPages: 1,
              },
            });
          } else {
            set({ builders: [], pagination: { total: 0, currentPage: 1, totalPages: 0 } });
          }
        } catch (error) {
          console.error("Error fetching builders:", error);
          set({ builders: [], pagination: { total: 0, currentPage: 1, totalPages: 0 } });
        } finally {
          set({ isLoading: false });
        }
      },

      fetchBuilderInfo: async (builderId) => {
        set({ isLoading: true, error: null });
        try {
          const response = await axiosInstancev1.get(`/user/builder/${builderId}`);
          if (response.data) {
            set({ builder: response.data });
          } else {
            set({ builder: null });
          }
        } catch (error) {
          console.error("Error fetching builder data:", error);
          set({ error: error.message, builder: null });
          toast.error("Failed to fetch builder data.");
        } finally {
          set({ isLoading: false });
        }
      },

      fetchMappedDistricts: async () => {
        set({ isLoading: true });
        try {
          const response = await axiosInstancev1.get("/address/mapped-districts");
          set({ mappedDistricts: response.data || [] });
        } catch (error) {
          console.error("Error fetching mapped districts:", error);
          set({ mappedDistricts: [] });
        } finally {
          set({ isLoading: false });
        }
      },

      fetchMappedTaluks: async (district) => {
        if (!district) return;
        set({ isLoading: true });
        try {
          const response = await axiosInstancev1.get(
            `/address/mapped-taluks?district=${encodeURIComponent(district)}`
          );
          set({ mappedTaluks: response.data || [] });
        } catch (error) {
          console.error("Error fetching mapped taluks:", error);
          set({ mappedTaluks: [] });
        } finally {
          set({ isLoading: false });
        }
      },

      sendProjectInvite: async (inviteData) => {
        const token = useAuthStore.getState().token;
        if (!token) {
          toast.error("Missing authentication.");
          return { success: false, message: "Authentication required." };
        }
        try {
          const response = await axiosInstancev1.post(
            "/customer/invite/project-invite",
            inviteData,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          return {
            success: true,
            message: response.data.message || "Project invite sent successfully!",
          };
        } catch (error) {
          const errorMsg = error.response?.data?.message || "Failed to send project invite.";
          return { success: false, message: errorMsg };
        }
      },
    }),
    {
      name: "find-builder-storage",
      getStorage: () => localStorage,
      partialize: (state) => ({
        selectedState: state.selectedState,
        selectedDistrict: state.selectedDistrict,
        selectedTaluk: state.selectedTaluk,
      }),
    }
  )
);

export default useFindBuilderStore;
